import { useEffect, useMemo, useState, type ReactNode } from "react"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate, useSearchParams } from "react-router-dom"
import { CalendarClock, ExternalLink, Flag, Mail, Phone, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"
import { toast } from "@/components/portal/Toast"
import { HeroVorgangName } from "@/components/portal/HeroVorgangName"
import { FlagNoteModal } from "@/components/portal/FlagNoteModal"
import { ReviewCard } from "@/components/review/ReviewCard"
import { planAbfrage } from "@/components/review/planAbfrage"
import { TodoActionModal, type PrepareResult } from "@/components/review/TodoActionModal"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { api } from "@/lib/api"
import { personName, cn } from "@/lib/utils"
import { betroffenerName, proactiveLabel } from "@/components/review/proaktiv"
import type { DraftListItem, DraftListResponse, DraftDetail, HandlingClass } from "@/components/review/types"
import { AnomalienTab } from "@/components/review/AnomalienTab"
import { InterneAufgabenListe } from "@/pages/InterneAufgabenPage"
import { tabAusSuche, sucheMitTab, type Channel } from "./reviewTabs"
import { reviewAuswahl, nachHerkunft, nachbarId, nachladeHinweis, reiterZahl, NACHLADEN_SCHRITT, LIMIT_MAX } from "./reviewAuswahl"

function TenantDropdown({ tenant, onTenantChange }: { tenant: string; onTenantChange: (v: string) => void }) {
  const { data } = useQuery({
    queryKey: ["tenants-dropdown"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/tenants", {})
      if (error) throw new Error(String(error))
      return data
    },
    staleTime: 1000 * 60 * 5,
  })
  return (
    <select
      value={tenant}
      onChange={(e) => onTenantChange(e.target.value)}
      style={{ width: "auto", padding: "4px 8px", fontSize: 12.5, height: 28 }}
    >
      <option value="">Alle Tenants</option>
      {data?.items.map((t) => (
        <option key={t.slug} value={t.slug}>{t.name}</option>
      ))}
    </select>
  )
}


type PipelineAlert = {
  id: number
  tenant_slug: string
  tenant_name: string
  project_id: number
  project_title: string | null
  project_nr: string | null
  step_id: number | null
  step_name: string | null
  todo: string | null
  project_type_id: number | null
  project_type_name: string
  action_kind: "email" | "call" | "hero" | "termin"
  action_label: string
  action_reason: string
  alt_kind: "" | "email" | "call" | "hero" | "termin"
  alt_label: string
  prepared_email_id: number | null
  days_in_step: number | null
  threshold: number | null
  threshold_unit: string | null
  priority: "low" | "medium" | "high" | "critical"
  detected_at: string | null
  hero_contact_id: number | null
  hero_contact_name: string | null
  contact_email: string | null
  contact_phone: string | null
  contact_street: string | null
  contact_city: string | null
  contact_postcode: string | null
}

async function fetchDrafts(tenant: string, klass: HandlingClass | "all", limit: number): Promise<DraftListResponse> {
  const params = new URLSearchParams()
  if (tenant) params.set("tenant", tenant)
  params.set("handling_class", klass)
  // Obergrenze JE REITER — „Aeltere laden" hebt sie.
  params.set("limit", String(limit))
  const r = await fetch(`/api/v1/drafts?${params.toString()}`, { credentials: "include" })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

async function fetchDraftDetail(emailId: number): Promise<DraftDetail> {
  const r = await fetch(`/api/v1/drafts/${emailId}`, { credentials: "include" })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

async function fetchPipelineAlerts(tenant: string): Promise<{ items: PipelineAlert[]; total: number }> {
  const params = new URLSearchParams()
  if (tenant) params.set("tenant", tenant)
  const r = await fetch(`/api/v1/review/projects?${params.toString()}`, { credentials: "include" })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

async function resolveAlert(id: number): Promise<void> {
  const r = await fetch(`/api/v1/review/projects/${id}/resolve`, { method: "POST", credentials: "include" })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
}

async function prepareTodo(alertId: number, kind?: string): Promise<PrepareResult> {
  const q = kind ? `?kind=${encodeURIComponent(kind)}` : ""
  const r = await fetch(`/api/v1/review/projects/${alertId}/prepare${q}`, {
    method: "POST", credentials: "include",
  })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

type ContextItem = {
  kind: "email" | "call" | "termin" | "switch"
  at: string | null
  direction: string
  title: string
  snippet: string
  url: string
}

type AlertContext = {
  first_touch: ContextItem | null
  items: ContextItem[]
  email_count: number
  call_count: number
  termin_count: number
  step_reason: string | null
  note: string
}

async function fetchAlertContext(alertId: number): Promise<AlertContext> {
  const r = await fetch(`/api/v1/review/projects/${alertId}/context`, { credentials: "include" })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

async function triggerScan(tenant: string): Promise<{ ok: boolean; new: number; resolved: number }> {
  const params = new URLSearchParams()
  if (tenant) params.set("tenant", tenant)
  const r = await fetch(`/api/v1/review/projects/scan?${params.toString()}`, { method: "POST", credentials: "include" })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

const CLASS_META: Record<HandlingClass, { label: string; color: string; bg: string; border: string }> = {
  antworten: { label: "Antworten", color: "#9A3412", bg: "#FFEDD5", border: "#FDBA74" },
  aufgabe:   { label: "Aufgabe",   color: "#854D0E", bg: "#FEF3C7", border: "#FDE68A" },
  info:      { label: "Info",      color: "#166534", bg: "#DCFCE7", border: "#86EFAC" },
  newsletter:{ label: "Newsletter",color: "#475569", bg: "#F1F5F9", border: "#CBD5E1" },
}

/**
 * Review-Page — ersetzt Smart Inbox.
 * Filter-Pills oben (Antworten/Aufgabe/Info/Newsletter), default = Antworten.
 */
export default function ReviewPage() {
  const [tenant, setTenant] = useTenantFilter()
  const [search, setSearch] = useSearchParams()
  const selectedId = search.get("id") ? parseInt(search.get("id")!) : null
  const navigate = useNavigate()
  // Reiter steht in der URL, damit der Zurueck-Knopf aus dem Mailprogramm
  // wieder bei den Projekten landet und nicht bei den E-Mails.
  const channel: Channel = tabAusSuche(search)
  const setChannel = (c: Channel) => {
    setSearch(sucheMitTab(search, c), { replace: true })
  }
  const [activeClass, setActiveClass] = useState<HandlingClass>("antworten")
  const [flaggedOnly, setFlaggedOnly] = useState(false)
  const qc = useQueryClient()

  // Flag-Toggle (rote Fahne) — markiert Mails die der User noch bearbeiten muss.
  // Bleibt aktiv auch nach Senden, wird nur durch User-Klick wieder entfernt.
  // Beim Entfernen wird die Notiz mit-geloescht.
  const flagMutation = useMutation({
    mutationFn: async (args: { emailId: number; flagged: boolean; note?: string | null }) => {
      const r = await fetch(`/api/v1/emails/${args.emailId}/flag`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ flagged: args.flagged, note: args.note ?? null }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json() as Promise<{ ok: boolean; flagged: boolean; note: string | null }>
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["drafts"] })
      qc.invalidateQueries({ queryKey: ["draft-detail"] })
    },
    onError: (e) => toast("Markierung fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })
  const projectAlerts = useQuery({
    queryKey: ["pipeline-alerts", tenant],
    queryFn: () => fetchPipelineAlerts(tenant),
    enabled: channel === "projects",
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 60,  // jede Minute
  })

  // Immer alle Reiter auf einmal (#164): der Server holt ohnehin dieselben
  // Zeilen und filtert erst danach nach Reiter — so kennt „Markiert" auch die
  // markierten Mails der anderen Reiter.
  // „Aeltere laden": hebt das Limit je Reiter. Eine Abfrage statt Seiten —
  // so bleibt es beim Pollen bei einer Liste ohne Dubletten, und markierte
  // Mails kommen wie gehabt ueber die Sonderabfrage des Servers dazu.
  const [limit, setLimit] = useState(NACHLADEN_SCHRITT)
  useEffect(() => { setLimit(NACHLADEN_SCHRITT) }, [tenant])
  const list = useQuery({
    queryKey: ["drafts", tenant, "all", limit],
    queryFn: () => fetchDrafts(tenant, "all", limit),
    // Beim Nachladen die bisherige Liste stehen lassen, kein „Lade…".
    placeholderData: keepPreviousData,
    staleTime: 1000 * 10,
    refetchInterval: 1000 * 30,  // alle 30 Sek neue Mails laden
  })

  // Nur die Zahl fuer den Reiter (#24) — die Liste selbst laedt der Reiter
  // erst, wenn er offen ist. Ein Bereich, den man vielleicht nie anklickt,
  // soll nicht bei jedem Seitenaufruf 272 Belege ziehen.
  const anomalienAnzahl = useQuery<{ total: number }>({
    queryKey: ["anomalien-anzahl", tenant],
    queryFn: async () => {
      const p = tenant ? `?tenant=${encodeURIComponent(tenant)}` : ""
      const r = await fetch(`/api/v1/anomalien/anzahl${p}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    staleTime: 1000 * 60 * 5,
  })

  // #13: Zahl der offenen internen Aufgaben fuer den Reiter. Gleicher
  // Endpunkt wie die Liste — die Zahl zaehlt genau, was der Reiter zeigt.
  const interneAnzahl = useQuery<{ count: number }>({
    queryKey: ["portal-aufgaben", "anzahl", tenant || ""],
    queryFn: async () => {
      const p = new URLSearchParams({ offen: "true" })
      if (tenant) p.set("tenant", tenant)
      const r = await fetch(`/api/v1/portal-aufgaben?${p}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    staleTime: 1000 * 30,
  })

  const allItems = useMemo(() => list.data?.items ?? [], [list.data])
  // Herkunfts-Filter (#26): bei Sanitec standen 9 proaktive gegen 52 echte
  // offene Zeilen — ohne Filter gehen die Portal-Aufgaben schlicht unter.
  const [herkunft, setHerkunft] = useState<"alle" | "extern" | "portal">("alle")
  // Erst Reiter (oder „Markiert" ueber alle Reiter), dann Herkunft.
  const reiterItems = useMemo(
    () => reviewAuswahl(allItems, { klasse: activeClass, nurMarkierte: flaggedOnly }),
    [allItems, activeClass, flaggedOnly],
  )
  const items = useMemo(() => nachHerkunft(reiterItems, herkunft), [reiterItems, herkunft])
  const hinweis = useMemo(
    () => nachladeHinweis(list.data, { klasse: activeClass, nurMarkierte: flaggedOnly }),
    [list.data, activeClass, flaggedOnly],
  )
  const aeltereLaden = () => setLimit((l) => Math.min(l + NACHLADEN_SCHRITT, LIMIT_MAX))
  const flaggedCount = useMemo(() => allItems.filter((i) => i.flagged).length, [allItems])
  const portalCount = useMemo(() => reiterItems.filter((i) => i.is_proactive).length, [reiterItems])

  const [flagModalItem, setFlagModalItem] = useState<DraftListItem | null>(null)

  const handleFlagClick = (item: DraftListItem) => {
    if (item.flagged) {
      // Entflaggen — keine Rueckfrage, schnell wegklicken
      flagMutation.mutate({ emailId: item.email_id, flagged: false })
    } else {
      setFlagModalItem(item)
    }
  }

  const lastIdKey = `review:lastId:${tenant || "all"}:${activeClass}`

  // Restore: zuletzt offene Mail wiederherstellen (pro Tenant+Klasse), sonst ersten Draft.
  //
  // NUR im E-Mails-Reiter. Der Effekt schreibt `?id=…` und wirft dabei alles
  // andere aus der URL — auch `tab=projects`. Ohne die Bremse sprang der
  // Projekte-Reiter sofort wieder auf E-Mails zurueck.
  useEffect(() => {
    if (channel !== "emails") return
    if (selectedId || items.length === 0 || list.isFetching) return
    const stored = parseInt(localStorage.getItem(lastIdKey) || "")
    const restored = stored && items.some((i: DraftListItem) => i.email_id === stored) ? stored : items[0].email_id
    setSearch({ id: String(restored) }, { replace: true })
  }, [channel, selectedId, items, list.isFetching, lastIdKey])

  // Persist: jede Auswahl merken
  useEffect(() => {
    if (selectedId) localStorage.setItem(lastIdKey, String(selectedId))
  }, [selectedId, lastIdKey])

  const selectedIdx = selectedId ? items.findIndex((i) => i.email_id === selectedId) : -1

  const goTo = (emailId: number) => setSearch({ id: String(emailId) })
  // #165: auch wenn die offene Mail nicht (mehr) in der Liste steht —
  // etwa weil ein Neuplanen sie in einen anderen Reiter geschoben hat.
  const goNext = () => {
    const id = nachbarId(items, selectedId, 1)
    if (id && id !== selectedId) goTo(id)
  }
  const goPrev = () => {
    const id = nachbarId(items, selectedId, -1)
    if (id && id !== selectedId) goTo(id)
  }
  const afterAction = () => {
    const next = items[selectedIdx + 1] || items[0]
    if (next && next.email_id !== selectedId) {
      goTo(next.email_id)
    } else {
      setSearch({}, { replace: true })
    }
  }

  const detail = useQuery({
    queryKey: ["draft-detail", selectedId],
    queryFn: () => fetchDraftDetail(selectedId!),
    enabled: !!selectedId,
    staleTime: 0,
  })

  // Tempo (#158): vorher lief das Oeffnen streng nacheinander — Liste,
  // dann Detail, dann (erst nach dem Detail, in der ReviewCard) der Plan.
  // (1) Plan parallel zum Detail anstossen, sobald die ID feststeht. Die
  //     ReviewCard holt ihn ohnehin fuer jede geoeffnete Mail; gleicher
  //     Schluessel → kein zweiter Abruf.
  useEffect(() => {
    if (channel !== "emails" || !selectedId) return
    void qc.prefetchQuery(planAbfrage(selectedId))
  }, [channel, selectedId, qc])
  // (2) Waehrend die Liste noch laedt: das Detail der zuletzt offenen Mail
  //     schon holen (nur DB, kein Modell-Aufruf). Bestaetigt die Liste die
  //     ID, steht die Karte sofort da; sonst bleibt ein ungenutzter Abruf.
  //     Den Plan bewusst NICHT spekulativ — der kann einen Planer-Aufruf
  //     ausloesen.
  useEffect(() => {
    if (channel !== "emails" || selectedId || !list.isLoading) return
    const stored = parseInt(localStorage.getItem(lastIdKey) || "")
    if (!stored) return
    void qc.prefetchQuery({ queryKey: ["draft-detail", stored], queryFn: () => fetchDraftDetail(stored) })
  }, [channel, selectedId, list.isLoading, lastIdKey, qc])

  const headerCount = channel === "emails" ? list.data?.total
    : channel === "interne" ? interneAnzahl.data?.count
    : projectAlerts.data?.total

  const refreshing = channel === "emails" ? list.isFetching : projectAlerts.isFetching
  const onRefresh = () => channel === "emails" ? list.refetch()
    : channel === "interne" ? qc.invalidateQueries({ queryKey: ["portal-aufgaben"] })
    : projectAlerts.refetch()

  return (
    <>
      {/* Kompakter Header: Titel + Tenant-Filter + Tabs + Refresh in einer Zeile */}
      <div style={{
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12,
        padding: "10px 0", borderBottom: "0.5px solid rgba(44,44,42,0.08)",
        marginBottom: 4,
      }}>
        <h2 style={{
          fontSize: 16, fontWeight: 700, margin: 0, color: "var(--text)",
          display: "inline-flex", alignItems: "baseline", gap: 6,
        }}>
          Review
          {headerCount !== undefined && (
            <span style={{ fontSize: 13, fontWeight: 400, color: "var(--text-muted)" }}>
              ({headerCount})
            </span>
          )}
        </h2>
        <TenantDropdown tenant={tenant} onTenantChange={setTenant} />
        <div style={{ display: "flex", gap: 4, marginLeft: 4 }}>
          {([
            { key: "emails", label: "E-Mails", count: list.data?.total },
            { key: "projects", label: "Projekte", count: projectAlerts.data?.total },
            { key: "anomalien", label: "Anomalien", count: anomalienAnzahl.data?.total },
            { key: "interne", label: "Interne Aufgaben", count: interneAnzahl.data?.count },
          ] as { key: Channel; label: string; count?: number }[]).map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setChannel(t.key)}
              style={{
                background: channel === t.key ? "#3C3489" : "transparent",
                color: channel === t.key ? "#fff" : "#5F5E5A",
                border: "none", padding: "5px 12px", borderRadius: 6,
                fontSize: 12.5, fontWeight: channel === t.key ? 600 : 500, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 5,
              }}
            >
              {t.label}
              {typeof t.count === "number" && (
                <span style={{
                  background: channel === t.key ? "rgba(255,255,255,0.25)" : "rgba(44,44,42,0.08)",
                  fontSize: 12, padding: "1px 6px", borderRadius: 999, fontWeight: 600,
                }}>{t.count}</span>
              )}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="pp-btn pp-btn-sm"
          title="Aktualisieren"
          // Klick-Test 08.10.2026 (#165): bei 1440 px lag der Knopf unter den
          // drei schwebenden Knoepfen oben rechts (Problem melden, Interne
          // Aufgabe, Suche — bis 148 px vom rechten Rand) und war nicht
          // klickbar. Platz freihalten.
          style={{ padding: "5px 8px", marginRight: 120 }}
        >
          <RefreshCw className={cn("w-3 h-3", refreshing && "animate-spin")} />
        </button>
      </div>

      {channel === "anomalien" ? (
        <AnomalienTab tenant={tenant} />
      ) : channel === "interne" ? (
        <InterneAufgabenListe tenant={tenant} eingebettet />
      ) : channel === "projects" ? (
        <ProjectsTab
          alerts={projectAlerts.data?.items || []}
          geladen={projectAlerts.data !== undefined}
          loading={projectAlerts.isLoading}
          error={projectAlerts.isError ? projectAlerts.error : null}
          onResolve={async (id) => {
            await resolveAlert(id)
            qc.invalidateQueries({ queryKey: ["pipeline-alerts", tenant] })
            toast("✓ Alert geloest")
          }}
          onScan={async () => {
            const stats = await triggerScan(tenant)
            qc.invalidateQueries({ queryKey: ["pipeline-alerts", tenant] })
            toast(`✓ Scan: ${stats.new} neu, ${stats.resolved} aufgelöst`)
          }}
          onOpenEmail={(emailId) => {
            // Ins Mailprogramm, nicht in die Review-Karte: dort ist der ganze
            // Verlauf zu sehen und der Entwurf steht im Antwort-Feld.
            // Der Rueckweg traegt die aktuellen Filter mit, damit der
            // Zurueck-Knopf genau dorthin fuehrt, wo man losgegangen ist.
            const zurueck = `/review?${new URLSearchParams(search).toString()}`
            // Mandant mitgeben — sonst steht die Mailliste auf einem anderen
            // Betrieb als die geoeffnete Mail (25.08.2026).
            navigate(`/emails?open=${emailId}&tenant=${encodeURIComponent(tenant || "")}`
                     + `&from=${encodeURIComponent(zurueck)}`)
          }}
          onPrepared={() => qc.invalidateQueries({ queryKey: ["pipeline-alerts", tenant] })}
        />
      ) : (
        <EmailsTab
          list={list}
          items={items}
          listData={list.data}
          hinweis={hinweis}
          aeltereLaden={aeltereLaden}
          kannMehr={limit < LIMIT_MAX}
          activeClass={activeClass}
          setActiveClass={setActiveClass}
          setSearch={setSearch}
          selectedId={selectedId}
          detail={detail}
          goTo={goTo}
          goNext={goNext}
          goPrev={goPrev}
          afterAction={afterAction}
          flaggedOnly={flaggedOnly}
          setFlaggedOnly={setFlaggedOnly}
          flaggedCount={flaggedCount}
          onFlagClick={handleFlagClick}
          herkunft={herkunft}
          setHerkunft={setHerkunft}
          portalCount={portalCount}
          gesamtCount={reiterItems.length}
        />
      )}
      <FlagNoteModal
        open={flagModalItem !== null}
        subject={flagModalItem?.subject || ""}
        initialNote={flagModalItem?.flag_note || ""}
        onSave={(note) => {
          if (!flagModalItem) return
          flagMutation.mutate({
            emailId: flagModalItem.email_id,
            flagged: true,
            note: note || null,
          })
          setFlagModalItem(null)
        }}
        onClose={() => setFlagModalItem(null)}
      />
    </>
  )
}

function EmailsTab({
  list, items, listData, hinweis, aeltereLaden, kannMehr, activeClass, setActiveClass, setSearch,
  selectedId, detail, goTo, goNext, goPrev, afterAction,
  flaggedOnly, setFlaggedOnly, flaggedCount, onFlagClick,
  herkunft, setHerkunft, portalCount, gesamtCount,
}: any) {
  // Liste einklappen — wie die Ordnerspalte bei den Mails. Unter 1200 px ist
  // sie beim Schreiben einer Antwort eher im Weg als nuetzlich.
  const LISTE_ZU_AB = 1200
  const [listeEingeklappt, setListeEingeklappt] = useState(() => {
    const g = localStorage.getItem("review_liste")
    if (g === "zu") return true
    if (g === "auf") return false
    return window.innerWidth < LISTE_ZU_AB
  })
  useEffect(() => {
    let warSchmal = window.innerWidth < LISTE_ZU_AB
    const onResize = () => {
      const schmal = window.innerWidth < LISTE_ZU_AB
      if (schmal !== warSchmal) {
        warSchmal = schmal
        localStorage.removeItem("review_liste")
        setListeEingeklappt(schmal)
      }
    }
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])
  return (
    <>
      <div style={{
        display: "flex", gap: 6, padding: "6px 0", flexWrap: "wrap", alignItems: "center",
      }}>
        {(["antworten", "aufgabe", "info", "newsletter"] as HandlingClass[]).map((c) => {
          const meta = CLASS_META[c]
          // Echte Zahl vom Server — unbekannt: keine Zahl, nie eine falsche 0.
          const n = reiterZahl(listData, c)
          const active = activeClass === c
          return (
            <button
              key={c}
              type="button"
              onClick={() => { setActiveClass(c); setSearch({}, { replace: true }) }}
              style={{
                background: active ? meta.bg : "#fff",
                color: active ? meta.color : "#5F5E5A",
                border: `0.5px solid ${active ? meta.border : "rgba(44,44,42,0.15)"}`,
                padding: "3px 10px", borderRadius: 999, fontSize: 12,
                fontWeight: active ? 600 : 500, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 5,
              }}
            >
              <span style={{
                width: 6, height: 6, borderRadius: "50%", background: meta.border,
              }} />
              {meta.label}
              {n !== null && (
                <span style={{
                  background: active ? meta.color : "rgba(44,44,42,0.06)",
                  color: active ? "#fff" : "#5F5E5A",
                  fontSize: 12, padding: "0px 6px", borderRadius: 999, fontWeight: 600,
                }}>{n}</span>
              )}
            </button>
          )
        })}
        <button
          type="button"
          onClick={() => setFlaggedOnly(!flaggedOnly)}
          title="Nur markierte Mails anzeigen (rote Fahne) — aus allen Reitern"
          style={{
            marginLeft: 4,
            background: flaggedOnly ? "rgba(220,38,38,0.1)" : "#fff",
            color: flaggedOnly ? "#dc2626" : "#5F5E5A",
            border: `0.5px solid ${flaggedOnly ? "#dc2626" : "rgba(44,44,42,0.15)"}`,
            padding: "3px 10px", borderRadius: 999, fontSize: 12,
            fontWeight: flaggedOnly ? 600 : 500, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 5,
          }}
        >
          <Flag size={11} fill={flaggedOnly ? "#dc2626" : "transparent"} color={flaggedOnly ? "#dc2626" : "#888780"} strokeWidth={flaggedOnly ? 0 : 1.8} />
          Markiert
          <span style={{
            background: flaggedOnly ? "#dc2626" : "rgba(44,44,42,0.06)",
            color: flaggedOnly ? "#fff" : "#5F5E5A",
            fontSize: 12, padding: "0px 6px", borderRadius: 999, fontWeight: 600,
          }}>{flaggedCount}</span>
        </button>
        {/* Herkunfts-Filter (#26). Bei Sanitec standen 9 Portal-Zeilen gegen 52
            echte offene — ohne Filter sind sie nicht auffindbar, und genau das
            war die Meldung: „Dann muss das ganz klar ersichtlich sein, wenn
            etwas eine Aufgabe vom Portal ist und keine E-Mail vom Kunden."
            Nur zeigen, wenn es ueberhaupt Portal-Zeilen gibt — ein Filter, der
            immer null trifft, ist nur ein Knopf mehr. */}
        {portalCount > 0 && (["portal", "extern"] as const).map((wahl) => {
          const aktiv = herkunft === wahl
          const text = wahl === "portal" ? "Nur Portal" : "Nur von aussen"
          return (
            <button
              key={wahl}
              type="button"
              onClick={() => setHerkunft(aktiv ? "alle" : wahl)}
              title={wahl === "portal"
                ? "Nur Zeilen, die das Portal selbst erzeugt hat"
                : "Nur echte Nachrichten von aussen"}
              style={{
                background: aktiv ? "#EEF2FF" : "#fff",
                color: aktiv ? "#3C3489" : "#5F5E5A",
                border: `0.5px solid ${aktiv ? "#C7D2FE" : "rgba(44,44,42,0.15)"}`,
                padding: "3px 10px", borderRadius: 999, fontSize: 12,
                fontWeight: aktiv ? 600 : 500, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 5,
              }}
            >
              {text}
              <span style={{
                background: aktiv ? "#3C3489" : "rgba(44,44,42,0.06)",
                color: aktiv ? "#fff" : "#5F5E5A",
                fontSize: 12, padding: "0px 6px", borderRadius: 999, fontWeight: 600,
              }}>
                {wahl === "portal" ? portalCount : gesamtCount - portalCount}
              </span>
            </button>
          )
        })}
      </div>

      {list.isError ? (
        <ErrorBox error={list.error} />
      ) : list.isLoading ? (
        <Loading label="Lade Drafts…" />
      ) : items.length === 0 ? (
        <div className="pp-card" style={{ padding: 48, textAlign: "center", color: "var(--text-muted)" }}>
          {flaggedOnly ? "Keine markierten Mails." : <>Keine offenen {CLASS_META[activeClass].label}-Items. 🎉</>}
        </div>
      ) : (
        <div
          className={listeEingeklappt ? "review-layout ohne-liste" : "review-layout"}
          style={{ gap: 14, height: "calc(100vh - 130px)", minHeight: 500 }}
        >
          {listeEingeklappt && (
            <button
              type="button"
              onClick={() => { setListeEingeklappt(false); localStorage.setItem("review_liste", "auf") }}
              title={`Liste einblenden (${items.length})`}
              aria-label="Liste einblenden"
              aria-expanded={false}
              className="review-liste-auf"
            >
              <ChevronRight style={{ width: 14, height: 14 }} />
              <span>{items.length}</span>
            </button>
          )}
          {/* Liste — einklappbar wie die Ordnerspalte bei den Mails.
              Beim Schreiben einer Antwort braucht man sie nicht; eingeklappt
              bekommt die Karte daneben die volle Breite. */}
          <div className="pp-card" style={{
            padding: 0, display: listeEingeklappt ? "none" : "flex",
            flexDirection: "column", overflow: "hidden",
          }}>
            <div style={{
              padding: "10px 14px", borderBottom: "0.5px solid rgba(44,44,42,0.08)",
              fontSize: 12, color: "#888780", textTransform: "uppercase", letterSpacing: 0.5,
              fontWeight: 500, display: "flex", alignItems: "center", gap: 8,
            }}>
              <span style={{ flex: 1 }}>{items.length} {flaggedOnly ? "Markiert" : CLASS_META[activeClass].label}</span>
              <button
                type="button"
                onClick={() => { setListeEingeklappt(true); localStorage.setItem("review_liste", "zu") }}
                title="Liste ausblenden"
                aria-label="Liste ausblenden"
                aria-expanded
                style={{
                  background: "none", border: 0, cursor: "pointer", color: "#888780",
                  padding: 2, display: "flex", lineHeight: 0,
                }}
              >
                <ChevronLeft style={{ width: 14, height: 14 }} />
              </button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {hinweis && (
                <div data-testid="nachlade-hinweis" style={{
                  padding: "8px 14px", fontSize: 12.5, background: "#FEF3C7", color: "#854D0E",
                  borderBottom: "0.5px solid #FDE68A", display: "flex", flexWrap: "wrap",
                  alignItems: "center", gap: 8,
                }}>
                  {hinweis.fehlt && (
                    <span>
                      {hinweis.gesamt !== null
                        ? `${hinweis.geladen} von ${hinweis.gesamt} geladen`
                        : "Nicht alle geladen"}
                    </span>
                  )}
                  {hinweis.aelter ? (
                    <span>{hinweis.aelter} davon älter als 30 Tage</span>
                  ) : null}
                  {hinweis.fehlt && kannMehr && (
                    <button
                      type="button"
                      onClick={aeltereLaden}
                      disabled={list.isFetching}
                      style={{
                        background: "#fff", border: "0.5px solid #FDE68A", color: "#854D0E",
                        borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 600,
                        cursor: list.isFetching ? "wait" : "pointer",
                      }}
                    >
                      {list.isFetching ? "Lade…" : "Ältere laden"}
                    </button>
                  )}
                </div>
              )}
              {items.filter((it) => (it.wartet_seit_tagen ?? 0) >= 5).length > 0 && (
                <div data-testid="wiedervorlage-hinweis" style={{ padding: "8px 14px", fontSize: 12.5, background: "#FCEBEB", color: "#791F1F", borderBottom: "0.5px solid #F09595" }}>
                  {items.filter((it) => (it.wartet_seit_tagen ?? 0) >= 5).length} Anfragen warten seit über 5 Tagen
                </div>
              )}
              {items.map((it) => (
                <DraftRow
                  key={it.email_id}
                  item={it}
                  selected={it.email_id === selectedId}
                  onClick={() => goTo(it.email_id)}
                  onFlagClick={() => onFlagClick(it)}
                />
              ))}
            </div>
          </div>

          {/* Review-Card */}
          <div style={{ minHeight: 0, overflow: "hidden" }}>
            {!selectedId || detail.isLoading ? (
              <Loading label="Lade Draft…" />
            ) : detail.isError ? (
              <ErrorBox error={detail.error} />
            ) : detail.data ? (
              <ReviewCard
                key={detail.data.email_id}
                detail={detail.data}
                nurMarkiert={items.some((i: DraftListItem) => i.email_id === detail.data.email_id && i.nur_markiert)}
                onNext={goNext}
                onPrev={goPrev}
                onDone={afterAction}
              />
            ) : null}
          </div>
        </div>
      )}
    </>
  )
}

function DraftRow({
  item,
  selected,
  onClick,
  onFlagClick,
}: {
  item: DraftListItem
  selected: boolean
  onClick: () => void
  onFlagClick: () => void
}) {
  const dotColor = { high: "#1D9E75", medium: "#D85A30", low: "#888780" }[item.confidence]
  const klass = item.handling_class || "antworten"
  const meta = CLASS_META[klass]
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%", textAlign: "left", padding: "12px 14px",
        background: selected ? "#F5F4F0" : "transparent",
        border: "none", borderBottom: "0.5px solid rgba(44,44,42,0.06)",
        borderLeft: `3px solid ${selected ? "#3C3489" : meta.border}`,
        cursor: "pointer", display: "flex", flexDirection: "column", gap: 4,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{
          width: 6, height: 6, borderRadius: "50%", background: dotColor, flexShrink: 0,
        }} />
        {/* 🔴 Herkunft VORNE (#26, gemeldet 24.08.2026).
            Die Marke stand vorher in der untersten Metazeile neben dem
            Mandantennamen — also nicht dort, wo man den Absender liest. Und
            sie nannte den Ausloeser („Nachhak"), was sich wie eine Kategorie
            liest, nicht wie „das kam nicht von einem Kunden".
            Die Verwechslung ging in beide Richtungen: „Nachhak: Melina
            Probetina <melinaprobetina@example.com>" sah aus wie eine Mail VON
            Melina, und „Trustlocal — Erinnerung: Anfrage von Björn Probekoch" sah
            aus wie eine Portal-Zeile, war aber eine echte Kundenmail. */}
        {(item.wartet_seit_tagen ?? 0) >= 3 && (
          <span
            title="Wiedervorlage: die Anfrage wartet seit Tagen ohne Bearbeitung. Wer sie anders erledigt hat (Anruf, HERO), muss nichts tun — die Nachschau schließt sie von selbst."
            data-testid="wiedervorlage"
            style={{
              flexShrink: 0, padding: "1px 6px", borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
              background: (item.wartet_seit_tagen ?? 0) >= 5 ? "#FCEBEB" : "#FAEEDA",
              color: (item.wartet_seit_tagen ?? 0) >= 5 ? "#A32D2D" : "#854F0B",
              border: `0.5px solid ${(item.wartet_seit_tagen ?? 0) >= 5 ? "#F09595" : "#FAC775"}`,
            }}
          >
            wartet seit {item.wartet_seit_tagen} Tagen
          </span>
        )}
        {item.is_proactive && (
          <span
            title="Vom Portal erzeugt — kam nicht von aussen"
            style={{
              flexShrink: 0, background: "#EEF2FF", color: "#3C3489",
              border: "0.5px solid #C7D2FE", padding: "1px 6px", borderRadius: 999,
              fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
            }}
          >
            Portal · {proactiveLabel(item.proactive_trigger)}
          </span>
        )}
        {/* Name statt „Name <adresse@…>": In der schmalen Liste verbrauchte die
            Adresse zwei Drittel der Zeile und wurde dann abgeschnitten —
            gerade der Name blieb unvollstaendig. Die volle Zeile steht im
            Tooltip und im Detail rechts. */}
        <span
          title={item.sender || item.sender_email || undefined}
          style={{ fontSize: 14, fontWeight: item.is_proactive ? 400 : 500, color: item.is_proactive ? "#5F5E5A" : "#2C2C2A", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {/* Bei Portal-Zeilen steht im Absenderfeld der Kunde, den es
              BETRIFFT — nicht der, der geschrieben hat. Das muss die Zeile
              sagen, sonst antwortet jemand auf etwas, das nie ankam. */}
          {item.is_proactive
            ? `betrifft ${betroffenerName(item.sender, item.sender_email)}`
            : personName(item.sender, item.sender_email)}
        </span>
        <span
          role="button"
          tabIndex={0}
          title={item.flagged ? (item.flag_note || "Markiert (Klick = entfernen)") : "Zur Bearbeitung markieren"}
          onClick={(e) => { e.stopPropagation(); onFlagClick() }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onFlagClick() }
          }}
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 22, height: 22, borderRadius: 4, cursor: "pointer", flexShrink: 0,
            background: item.flagged ? "rgba(220,38,38,0.1)" : "transparent",
          }}
        >
          <Flag
            size={13}
            fill={item.flagged ? "#dc2626" : "transparent"}
            color={item.flagged ? "#dc2626" : "#bbb8b1"}
            strokeWidth={item.flagged ? 0 : 1.8}
          />
        </span>
      </div>
      <div style={{
        fontSize: 12, color: "#2C2C2A", overflow: "hidden",
        textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {item.subject || "(ohne Betreff)"}
      </div>
      {/* Vorschau = eingegangene Mail. Der Entwurf steht rechts im Detail —
          stand er auch hier, begann jede Zeile mit derselben Anrede. */}
      {(item.mail_preview || item.draft_preview) && (
        <div style={{
          fontSize: 12, color: "#888780",
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}>
          {item.mail_preview || item.draft_preview}
        </div>
      )}
      <div style={{ fontSize: 12, color: "#888780", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        {/* Die Herkunfts-Marke stand frueher hier; sie ist nach oben gewandert
            (#26). Zweimal dieselbe Marke waere Platzverschwendung in einer
            Zeile, die ohnehin abgeschnitten wird. */}
        <span>{item.tenant_name}</span>
        {/* #164: steht nur wegen der Fahne hier — sonst waere sie raus. */}
        {item.nur_markiert && (
          <span
            data-testid="nur-markiert"
            title="Ohne Entwurf oder schon beantwortet — steht wegen der Markierung im Review. Fahne entfernen oder erledigen nimmt sie heraus."
            style={{ color: "#5F5E5A" }}
          >· markiert</span>
        )}
      </div>
    </button>
  )
}

/**
 * Der betroffene Kunde einer Portal-Zeile, ohne das Auslöser-Praefix.
 *
 * 🔴 In `email_log.sender` steht bei proaktiven Zeilen der echte Kundenname
 * MIT vorangestelltem Auslöser: „Nachhak: Melina Probetina
 * <melinaprobetina@example.com>". Das Praefix sitzt mitten im Namen und wirkt wie
 * ein Betreff-Praefix — es war die einzige Unterscheidung zu einer echten
 * Kundenmail. Fuer die Anzeige gehoert es weg: die Herkunft sagt jetzt die
 * Marke, der Name soll der Name sein.
 */
// `betroffenerName` und `proactiveLabel` liegen in components/review/proaktiv.tsx
// — die Review-Karte braucht sie auch.
export { betroffenerName }

const PRIORITY_META: Record<PipelineAlert["priority"], { color: string; bg: string; border: string; label: string }> = {
  critical: { color: "#991B1B", bg: "#FEE2E2", border: "#FCA5A5", label: "Kritisch" },
  high:     { color: "#9A3412", bg: "#FFEDD5", border: "#FDBA74", label: "Hoch" },
  medium:   { color: "#854D0E", bg: "#FEF3C7", border: "#FDE68A", label: "Mittel" },
  low:      { color: "#475569", bg: "#F1F5F9", border: "#CBD5E1", label: "Niedrig" },
}

/** Anzeige-Label des Pipeline-Steps — zugleich der Schluessel des Status-Filters.
 *  Bewusst der Name, nicht die step_id: bei "Alle Tenants" hat derselbe Status
 *  je Betrieb eine andere step_id, der User denkt aber in "Rechnung erstellen". */
function stepLabelOf(a: PipelineAlert): string {
  return (a.step_name || (a.step_id ? `Step #${a.step_id}` : "—")).trim()
}

/** Projekttyp-Label. Jeder Typ hat eigene Steps — „Rechnung erstellen" gibt es
 *  bei Sanitec in zwei Typen — deshalb wird zuerst danach geclustert. */
function typeLabelOf(a: PipelineAlert): string {
  return a.project_type_name?.trim()
    || (a.project_type_id ? `Typ #${a.project_type_id}` : "Ohne Projekttyp")
}

const ACTION_ICON: Record<PipelineAlert["action_kind"], ReactNode> = {
  email: <Mail size={12} />,
  call: <Phone size={12} />,
  hero: <ExternalLink size={12} />,
  // Termin steht im Kalender — die Karte ist eine Wartelage, keine Aufgabe
  termin: <CalendarClock size={12} />,
}

const PRIO_RANK: Record<PipelineAlert["priority"], number> = {
  critical: 0, high: 1, medium: 2, low: 3,
}

/** Innerhalb einer Gruppe: kritischer zuerst, bei gleicher Prioritaet das
 *  laenger Liegengebliebene. */
function byUrgency(a: PipelineAlert, b: PipelineAlert): number {
  return PRIO_RANK[a.priority] - PRIO_RANK[b.priority] ||
    (b.days_in_step ?? 0) - (a.days_in_step ?? 0)
}

function ProjectsTab({
  alerts, geladen, loading, error, onResolve, onScan, onOpenEmail, onPrepared,
}: {
  alerts: PipelineAlert[]
  /** #200: erst wenn die Vorgaenge da sind, darf ein Filter als „gibt es nicht" gelten. */
  geladen: boolean
  loading: boolean
  error: unknown
  onResolve: (id: number) => Promise<void>
  onScan: () => Promise<void>
  onOpenEmail: (emailId: number) => void
  onPrepared: () => void
}) {
  const [scanning, setScanning] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [modal, setModal] = useState<{ result: PrepareResult; contact: string } | null>(null)

  // „To-do ausfuehren": Mail-Aktionen springen direkt in den fertigen Draft,
  // Anruf/HERO oeffnen das Modal — dort ist nichts zu senden, nur zu tun.
  const runTodo = async (a: PipelineAlert, kind?: string) => {
    if (!kind && a.action_kind === "email" && a.prepared_email_id) {
      onOpenEmail(a.prepared_email_id)
      return
    }
    setBusyId(a.id)
    try {
      const res = await prepareTodo(a.id, kind)
      if (res.kind === "email" && res.email_id) {
        onPrepared()
        onOpenEmail(res.email_id)
        toast(
          res.draft_kept
            ? "An dieser Mail lag schon ein Entwurf — der bleibt stehen"
            : res.mode === "reply"
              ? "✓ Entwurf liegt als Antwort auf die letzte Mail im Verlauf"
              : "✓ Entwurf angelegt — zu diesem Kontakt gibt es keinen Mailverlauf",
          res.draft_kept ? "error" : undefined,
        )
      } else {
        setModal({ result: res, contact: a.hero_contact_name || "" })
      }
    } catch (e) {
      toast("Vorbereiten fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error")
    } finally {
      setBusyId(null)
    }
  }
  // Filter in der URL statt im State: nur so sind sie noch da, wenn man aus
  // dem Mailprogramm zurueckkommt — vorher stand man wieder vor der
  // ungefilterten Liste und musste sich neu durchklicken.
  const [search, setSearch] = useSearchParams()
  const statusFilter = search.get("status") || ""
  const typeFilter = search.get("typ") || ""
  const setFilter = (key: "typ" | "status", wert: string) => {
    const next = new URLSearchParams(search)
    if (wert) next.set(key, wert)
    else next.delete(key)
    if (key === "typ") next.delete("status")   // anderer Typ, andere Steps
    setSearch(next, { replace: true })
  }
  const setStatusFilter = (v: string) => setFilter("status", v)
  const setTypeFilter = (v: string) => setFilter("typ", v)
  const [sortMode, setSortMode] = useState<"status" | "priority">("status")

  // Erst Projekttyp, dann Status — die Statusleiste zeigt nur die Steps des
  // gewaehlten Typs. Flach nebeneinander waren es 18 Pills, darunter zweimal
  // „Rechnung erstellen" aus verschiedenen Typen.
  const typeOptions = useMemo(() => {
    const m = new Map<string, { n: number; prio: PipelineAlert["priority"] }>()
    for (const a of alerts) {
      const k = typeLabelOf(a)
      const cur = m.get(k)
      if (!cur) m.set(k, { n: 1, prio: a.priority })
      else {
        cur.n += 1
        if (PRIO_RANK[a.priority] < PRIO_RANK[cur.prio]) cur.prio = a.priority
      }
    }
    return [...m.entries()].sort((x, y) =>
      PRIO_RANK[x[1].prio] - PRIO_RANK[y[1].prio] ||
      y[1].n - x[1].n ||
      x[0].localeCompare(y[0]),
    )
  }, [alerts])

  // #200: NICHT waehrend des Ladens — da ist die Liste leer, und der Filter
  // aus der URL (Rueckweg aus dem Mailprogramm) waere weg, bevor er greift.
  useEffect(() => {
    if (geladen && typeFilter && !typeOptions.some(([label]) => label === typeFilter)) {
      setTypeFilter("")
    }
  }, [geladen, typeOptions, typeFilter])

  const byType = useMemo(
    () => typeFilter ? alerts.filter((a) => typeLabelOf(a) === typeFilter) : alerts,
    [alerts, typeFilter],
  )

  // Status-Filter aus den geladenen Alerts (kein extra Request). Reihenfolge
  // wie die Gruppen darunter: dringendster Status zuerst, dann nach Menge —
  // sonst zeigen Pills und Liste zwei verschiedene Prioritaeten.
  const statusOptions = useMemo(() => {
    const m = new Map<string, { n: number; prio: PipelineAlert["priority"] }>()
    for (const a of byType) {
      const k = stepLabelOf(a)
      const cur = m.get(k)
      if (!cur) m.set(k, { n: 1, prio: a.priority })
      else {
        cur.n += 1
        if (PRIO_RANK[a.priority] < PRIO_RANK[cur.prio]) cur.prio = a.priority
      }
    }
    return [...m.entries()].sort((x, y) =>
      PRIO_RANK[x[1].prio] - PRIO_RANK[y[1].prio] ||
      y[1].n - x[1].n ||
      x[0].localeCompare(y[0]),
    )
  }, [byType])

  // Typ- oder Tenant-Wechsel: der gewaehlte Status existiert dort evtl. gar
  // nicht → zurueck auf "Alle", sonst steht der User vor einer leeren Liste.
  useEffect(() => {
    if (geladen && statusFilter && !statusOptions.some(([label]) => label === statusFilter)) {
      setStatusFilter("")
    }
  }, [geladen, statusOptions, statusFilter])

  const filtered = useMemo(
    () => statusFilter ? byType.filter((a) => stepLabelOf(a) === statusFilter) : byType,
    [byType, statusFilter],
  )

  // Gruppierung: nach Status (Default — der User denkt in "was steht an") oder
  // nach Prioritaet (alte Ansicht). Status-Gruppen sind nach der hoechsten
  // Prioritaet darin sortiert, damit Kritisches trotzdem oben steht.
  const groups = useMemo(() => {
    if (sortMode === "priority") {
      const order: PipelineAlert["priority"][] = ["critical", "high", "medium", "low"]
      return order
        .map((p) => ({
          key: p,
          label: PRIORITY_META[p].label,
          typeLabel: "",   // Prioritaets-Ansicht kennt keine Typ-Ebene
          color: PRIORITY_META[p].color,
          items: filtered.filter((a) => a.priority === p).slice().sort(byUrgency),
        }))
        .filter((g) => g.items.length > 0)
    }
    // Zwei Ebenen: Projekttyp, darin Status. Der Typ-Kopf wird beim Rendern
    // nur gezeigt, wenn er wechselt — bei aktivem Typ-Filter faellt er weg.
    const m = new Map<string, PipelineAlert[]>()
    for (const a of filtered) {
      const k = `${typeLabelOf(a)}\u0000${stepLabelOf(a)}`
      const arr = m.get(k)
      if (arr) arr.push(a)
      else m.set(k, [a])
    }
    const typeRank = new Map<string, number>()
    for (const a of filtered) {
      const t = typeLabelOf(a)
      const r = PRIO_RANK[a.priority]
      if (!typeRank.has(t) || r < (typeRank.get(t) as number)) typeRank.set(t, r)
    }
    return [...m.entries()]
      .map(([key, list]) => {
        const items = list.slice().sort(byUrgency)
        const [typeLabel, label] = key.split("\u0000")
        return {
          key, label, typeLabel,
          color: PRIORITY_META[items[0].priority].color,
          items,
        }
      })
      .sort((a, b) =>
        (typeRank.get(a.typeLabel) ?? 9) - (typeRank.get(b.typeLabel) ?? 9) ||
        a.typeLabel.localeCompare(b.typeLabel) ||
        PRIO_RANK[a.items[0].priority] - PRIO_RANK[b.items[0].priority] ||
        b.items.length - a.items.length ||
        a.label.localeCompare(b.label),
      )
  }, [filtered, sortMode])

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "12px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
          Projekte die ueber dem Schwellenwert in einem Step liegen.
          Schwellenwerte: <a href="/context" style={{ color: "var(--accent-color)" }}>Kontext-Center → Pipeline</a>.
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ display: "flex", border: "0.5px solid rgba(44,44,42,0.15)", borderRadius: 6, overflow: "hidden" }}>
            {([
              { key: "status", label: "Nach Status" },
              { key: "priority", label: "Nach Priorität" },
            ] as { key: "status" | "priority"; label: string }[]).map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setSortMode(m.key)}
                style={{
                  background: sortMode === m.key ? "#3C3489" : "#fff",
                  color: sortMode === m.key ? "#fff" : "#5F5E5A",
                  border: "none", padding: "4px 10px", fontSize: 12,
                  fontWeight: sortMode === m.key ? 600 : 500, cursor: "pointer",
                }}
              >{m.label}</button>
            ))}
          </div>
          <button
            type="button"
            disabled={scanning}
            onClick={async () => { setScanning(true); try { await onScan() } finally { setScanning(false) } }}
            className="pp-btn pp-btn-sm"
          >{scanning ? "Scannt…" : "Jetzt scannen"}</button>
        </div>
      </div>

      {/* Zwei Filterebenen: erst Projekttyp, dann dessen Status. Punkt =
          dringendste Prioritaet darin, Zahl = Menge. */}
      {typeOptions.length > 1 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{
            fontSize: 12, color: "#888780", textTransform: "uppercase",
            letterSpacing: 0.5, fontWeight: 600, marginRight: 2,
          }}>Projekttyp</span>
          <StatusPill
            label="Alle"
            count={alerts.length}
            active={typeFilter === ""}
            onClick={() => setTypeFilter("")}
          />
          {typeOptions.map(([label, meta]) => (
            <StatusPill
              key={label}
              label={label}
              count={meta.n}
              dot={PRIORITY_META[meta.prio].border}
              active={typeFilter === label}
              onClick={() => setTypeFilter(typeFilter === label ? "" : label)}
            />
          ))}
        </div>
      )}
      {byType.length > 0 && statusOptions.length > 1 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{
            fontSize: 12, color: "#888780", textTransform: "uppercase",
            letterSpacing: 0.5, fontWeight: 600, marginRight: 2,
          }}>Status</span>
          <StatusPill
            label="Alle"
            count={byType.length}
            active={statusFilter === ""}
            onClick={() => setStatusFilter("")}
          />
          {statusOptions.map(([label, meta]) => (
            <StatusPill
              key={label}
              label={label}
              count={meta.n}
              dot={PRIORITY_META[meta.prio].border}
              active={statusFilter === label}
              onClick={() => setStatusFilter(statusFilter === label ? "" : label)}
            />
          ))}
        </div>
      )}

      {error ? (
        <ErrorBox error={error} />
      ) : loading ? (
        <Loading label="Lade Pipeline-Alerts…" />
      ) : alerts.length === 0 ? (
        <div className="pp-card" style={{ padding: 48, textAlign: "center", color: "var(--text-muted)" }}>
          Keine offenen Pipeline-Alerts. 🎉
        </div>
      ) : filtered.length === 0 ? (
        <div className="pp-card" style={{ padding: 48, textAlign: "center", color: "var(--text-muted)" }}>
          Kein Projekt im Status „{statusFilter}“.
        </div>
      ) : (
        groups.map((g, idx) => (
          <div key={g.key}>
            {/* Typ-Kopf nur beim Wechsel — und nicht, wenn ohnehin nach einem
                Typ gefiltert ist, dann steht er schon in der Filterleiste. */}
            {g.typeLabel && !typeFilter && g.typeLabel !== groups[idx - 1]?.typeLabel && (
              <div style={{
                fontSize: 13, fontWeight: 700, color: "#2C2C2A",
                borderBottom: "0.5px solid rgba(44,44,42,0.12)",
                paddingBottom: 5, marginBottom: 10, marginTop: idx === 0 ? 0 : 8,
              }}>
                {g.typeLabel}
              </div>
            )}
            <div style={{
              fontSize: 12, color: g.color, textTransform: "uppercase",
              letterSpacing: 0.5, fontWeight: 600, marginBottom: 8,
            }}>
              {g.label} · {g.items.length}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {g.items.map((a) => (
                <AlertCard
                  key={a.id}
                  alert={a}
                  busy={busyId === a.id}
                  onRun={() => runTodo(a)}
                  onRunAlt={() => runTodo(a, a.alt_kind)}
                  onResolve={() => onResolve(a.id)}
                />
              ))}
            </div>
          </div>
        ))
      )}
      <TodoActionModal
        open={modal !== null}
        result={modal?.result || null}
        contactName={modal?.contact || ""}
        onClose={() => setModal(null)}
      />
    </div>
  )
}

/**
 * Vorgeschichte zum Alert: wie kam der Kontakt rein, was lief seitdem, warum
 * steht das Projekt in diesem Step. Laedt erst beim Aufklappen — bei 80 Karten
 * waeren 80 Requests im Voraus Unsinn.
 */
function ContextBlock({ alertId }: { alertId: number }) {
  const q = useQuery({
    queryKey: ["alert-context", alertId],
    queryFn: () => fetchAlertContext(alertId),
    staleTime: 1000 * 60,
  })

  if (q.isLoading) {
    return <div style={{ fontSize: 12, color: "#888780", padding: "8px 0" }}>Lade Vorgeschichte…</div>
  }
  if (q.isError || !q.data) {
    return <div style={{ fontSize: 12, color: "#9A3412", padding: "8px 0" }}>Vorgeschichte nicht ladbar.</div>
  }
  const c = q.data

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "0.5px solid rgba(44,44,42,0.1)" }}>
      <div style={{
        fontSize: 12, color: "#888780", textTransform: "uppercase",
        letterSpacing: 0.5, fontWeight: 600, marginBottom: 8,
        display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
      }}>
        Vorgeschichte
        <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>
          {c.email_count} Mail{c.email_count === 1 ? "" : "s"} · {c.call_count} Anruf{c.call_count === 1 ? "" : "e"}
          {typeof c.termin_count === "number" ? ` · ${c.termin_count} Termin${c.termin_count === 1 ? "" : "e"}` : ""}
        </span>
      </div>

      {c.first_touch && (
        <div style={{ fontSize: 12, color: "#3F3E3A", marginBottom: 6 }}>
          <span style={{ color: "#888780" }}>Erstkontakt: </span>
          {c.first_touch.kind === "call" ? "Anruf" : "E-Mail"}
          {c.first_touch.direction ? ` (${c.first_touch.direction})` : ""}
          {c.first_touch.at ? ` am ${fmtDate(c.first_touch.at)}` : ""}
          {c.first_touch.title ? ` — ${c.first_touch.title}` : ""}
        </div>
      )}
      {c.step_reason && (
        <div style={{ fontSize: 12, color: "#3F3E3A", marginBottom: 6 }}>
          <span style={{ color: "#888780" }}>Status gesetzt durch: </span>{c.step_reason}
        </div>
      )}
      {c.note && (
        <div style={{ fontSize: 12, color: "#888780", marginBottom: 8, fontStyle: "italic" }}>
          {c.note}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {c.items.map((it, i) => (
          <a
            key={`${it.url}-${i}`}
            href={it.url}
            style={{
              display: "flex", gap: 8, alignItems: "flex-start", textDecoration: "none",
              background: "#F8F7F4", border: "0.5px solid rgba(44,44,42,0.08)",
              borderRadius: 6, padding: "7px 9px", color: "#2C2C2A",
            }}
          >
            <span style={{ marginTop: 1, flexShrink: 0, color: "#888780" }}>
              {it.kind === "call" ? <Phone size={12} />
                : it.kind === "termin" ? <CalendarClock size={12} />
                : <Mail size={12} />}
            </span>
            <span style={{ minWidth: 0, flex: 1 }}>
              <span style={{ fontSize: 12, fontWeight: 500, display: "block" }}>
                {it.title}
              </span>
              {it.snippet && (
                <span style={{
                  fontSize: 12, color: "#5F5E5A", display: "-webkit-box",
                  WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                }}>{it.snippet}</span>
              )}
            </span>
            <span style={{ fontSize: 12, color: "#888780", whiteSpace: "nowrap", flexShrink: 0 }}>
              {it.direction} · {fmtDate(it.at)}
            </span>
          </a>
        ))}
      </div>
    </div>
  )
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—"
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso.slice(0, 10)
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" })
}

function StatusPill({
  label, count, dot, active, onClick,
}: {
  label: string
  count: number
  dot?: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={active ? "Filter aufheben" : `Nur „${label}“ zeigen`}
      style={{
        background: active ? "#3C3489" : "#fff",
        color: active ? "#fff" : "#5F5E5A",
        border: `0.5px solid ${active ? "#3C3489" : "rgba(44,44,42,0.15)"}`,
        padding: "3px 10px", borderRadius: 999, fontSize: 12,
        fontWeight: active ? 600 : 500, cursor: "pointer",
        display: "inline-flex", alignItems: "center", gap: 5,
      }}
    >
      {dot && (
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: dot, flexShrink: 0 }} />
      )}
      {label}
      <span style={{
        background: active ? "rgba(255,255,255,0.25)" : "rgba(44,44,42,0.06)",
        color: active ? "#fff" : "#5F5E5A",
        fontSize: 12, padding: "0px 6px", borderRadius: 999, fontWeight: 600,
      }}>{count}</span>
    </button>
  )
}

function AlertCard({
  alert, busy, onRun, onRunAlt, onResolve,
}: {
  alert: PipelineAlert
  busy: boolean
  onRun: () => void
  onRunAlt: () => void
  onResolve: () => Promise<void>
}) {
  const [resolving, setResolving] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const meta = PRIORITY_META[alert.priority]
  const stepLabel = stepLabelOf(alert)
  // Nur zeigen wenn es mehr sagt als der Step-Name — bei Steps ohne eigene
  // Vorlage ist das To-Do genau der Step-Name, das doppelt sonst nur.
  const todo = alert.todo && alert.todo !== stepLabel ? alert.todo : null
  const days = alert.days_in_step ?? 0
  const daysLabel = days === 0 ? "heute erkannt" : `seit ${days} ${days === 1 ? "Tag" : "Tagen"} über Schwelle`
  const heroUrl = `https://login.hero-software.de/partner/Projects/view/${alert.project_id}`
  const portalProjectUrl = `/projekte/${alert.project_id}?tenant=${encodeURIComponent(alert.tenant_slug)}`
  const addressLine = [alert.contact_street, [alert.contact_postcode, alert.contact_city].filter(Boolean).join(" ")]
    .filter(Boolean).join(", ")
  // Immer aufklappbar: die Vorgeschichte (Mails, Anrufe, Status-Grund) wird
  // beim Aufklappen nachgeladen, auch wenn die Stammdaten duenn sind.
  const hasDetails = true
  return (
    <div className="pp-card" style={{
      padding: 14, borderLeft: `4px solid ${meta.border}`,
    }}>
      <div
        onClick={() => hasDetails && setExpanded((v) => !v)}
        style={{
          display: "grid", gridTemplateColumns: "1fr auto", gap: 16, alignItems: "center",
          cursor: hasDetails ? "pointer" : "default",
        }}
      ><div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <span style={{
          background: meta.bg, color: meta.color, border: `0.5px solid ${meta.border}`,
          fontSize: 12, padding: "2px 10px", borderRadius: 999, fontWeight: 600,
          textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap",
        }}>{meta.label}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3, color: "#2C2C2A",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {alert.hero_contact_name || "Projekt"}
            {/* #98: Kuerzel statt „#7284194"; fehlt es, schlaegt die Komponente nach */}
            <span style={{ color: "#888780", fontWeight: 400 }}> · <HeroVorgangName
              tenantSlug={alert.tenant_slug} projectId={alert.project_id} nr={alert.project_nr} /></span>
            {hasDetails && (
              <span style={{ color: "#B0AFA9", fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
                {expanded ? "▾" : "▸"}
              </span>
            )}
          </div>
          {todo && (
            <div style={{ margin: "1px 0 5px" }}>
              <span style={{
                background: "#EEF2FF", color: "#3C3489", border: "0.5px solid #C7D2FE",
                padding: "2px 9px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                display: "inline-block",
              }}>To-do: {todo}</span>
            </div>
          )}
          <div style={{ fontSize: 12, color: "#5F5E5A", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontWeight: 500, color: "#2C2C2A" }}>{stepLabel}</span>
            <span style={{ color: "#888780" }}>·</span>
            <span>{daysLabel}</span>
            {alert.threshold && (
              <>
                <span style={{ color: "#888780" }}>·</span>
                <span style={{ color: "#888780" }}>Schwelle {alert.threshold}{alert.threshold_unit === "h" ? " h" : " d"}</span>
              </>
            )}
            {alert.tenant_name && (
              <>
                <span style={{ color: "#888780" }}>·</span>
                <span style={{ color: "#888780" }}>{alert.tenant_name}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          disabled={busy}
          onClick={onRun}
          className="pp-btn pp-btn-sm"
          title={alert.action_reason}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
            // Nur echte Aktionen (Mail/Anruf) werden hervorgehoben. Der
            // HERO-Fall ist ein Hinweis, kein Angebot — der darf nicht so
            // aussehen als koennte das Portal etwas erledigen.
            ...(alert.action_kind === "hero" || alert.action_kind === "termin"
              ? { color: "#888780" }
              : { borderColor: "#C7D2FE", color: "#3C3489" }),
          }}
        >
          {busy ? "…" : <>{ACTION_ICON[alert.action_kind]} {
            alert.prepared_email_id ? "Entwurf öffnen" : alert.action_label
          }</>}
        </button>
        {/* Zweiter Kanal — „Kontakt aufnehmen" ist per Default ein Anruf, aber
            wenn eine Mailadresse da ist, gehoert die Wahl dem Menschen. */}
        {alert.alt_kind && (
          <button
            type="button"
            disabled={busy}
            onClick={onRunAlt}
            className="pp-btn pp-btn-sm"
            title={alert.alt_label}
            style={{ padding: "4px 7px", color: "#5F5E5A" }}
          >
            {ACTION_ICON[alert.alt_kind as PipelineAlert["action_kind"]]}
          </button>
        )}
        <a
          href={heroUrl}
          target="_blank" rel="noreferrer"
          className="pp-btn pp-btn-sm"
          style={{ textDecoration: "none" }}
        >HERO ↗</a>
        <button
          type="button"
          disabled={resolving}
          onClick={async () => { setResolving(true); try { await onResolve() } finally { setResolving(false) } }}
          className="pp-btn pp-btn-sm pp-btn-primary"
        >{resolving ? "…" : "Erledigt"}</button>
      </div>
      </div>
      {expanded && hasDetails && (
        <div style={{
          marginTop: 12, paddingTop: 12, borderTop: "0.5px solid rgba(44,44,42,0.1)",
          display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 14px",
          fontSize: 12, color: "#3F3E3A",
        }}>
          {alert.project_title && (
            <>
              <div style={{ color: "#888780" }}>Projekt</div>
              <div>{alert.project_title}</div>
            </>
          )}
          {alert.contact_email && (
            <>
              <div style={{ color: "#888780" }}>E-Mail</div>
              <div><a href={`mailto:${alert.contact_email}`} style={{ color: "#3C3489" }}>{alert.contact_email}</a></div>
            </>
          )}
          {alert.contact_phone && (
            <>
              <div style={{ color: "#888780" }}>Telefon</div>
              <div><a href={`tel:${alert.contact_phone}`} style={{ color: "#3C3489" }}>{alert.contact_phone}</a></div>
            </>
          )}
          {addressLine && (
            <>
              <div style={{ color: "#888780" }}>Adresse</div>
              <div>{addressLine}</div>
            </>
          )}
          <div style={{ gridColumn: "1 / -1", marginTop: 4, display: "flex", gap: 8 }}>
            <a href={portalProjectUrl} className="pp-btn pp-btn-sm" style={{ textDecoration: "none" }}>
              Portal-Projektdetails
            </a>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <ContextBlock alertId={alert.id} />
          </div>
        </div>
      )}
    </div>
  )
}
